import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { useToast } from '@/components/ToastContainer'
import { Button, Input, Select, Modal, Badge, Table, Pagination, Alert, Dropdown, Drawer, Spinner, DateRangePicker, Tooltip, cn, type DateRange, type TableColumn, type DropdownItem } from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { formatDate, formatCredits, formatNumber, formatCurrency } from '@/ui'
import { yesterday, SHARED_RANGE_PRESETS_WITH_ALL, ALL_PRESET_KEY } from '@/lib/dateRange'
import styles from './Users.module.css'

interface Slots {
  universal: number
  invitation: number
  thankyou: number
}

interface AdminUser {
  id: string
  email: string
  name: string | null
  role: string
  aiCredits: number
  slots: Slots
  createdAt: string
  _count: {
    invitations: number
    invitationsActivated: number
    thankYouCards: number
    thankYouCardsActivated: number
    aiGenerations: number // 누적 생성 수(삭제해도 유지)
    aiRetained: number     // 현재 보유 사진 수
  }
}

/** 가입자수 대비 비율. total이 0이면 표시하지 않는다. */
function pct(n: number, total: number): string | null {
  if (total <= 0) return null
  return `${((n / total) * 100).toFixed(1)}%`
}

/** 슬롯 3종 합계 */
function totalSlots(s: Slots | undefined): number {
  if (!s) return 0
  return s.universal + s.invitation + s.thankyou
}

/** "범용 N · 청첩장 N · 감사장 N" 텍스트 */
function slotsText(s: Slots | undefined): string {
  if (!s) return '-'
  return `범용 ${s.universal} · 청첩장 ${s.invitation} · 감사장 ${s.thankyou}`
}

/** 결제 주문번호 — 사이트 결제는 orderId, 스마트스토어는 smartstoreOrderId */
function orderNoText(d: Record<string, any>): string | null {
  return d.smartstoreOrderId ?? d.orderId ?? null
}

/**
 * audit 로그 한 줄 설명. 항목 활성화(payment.activate_items)는 활성화 개수·잔여 슬롯까지
 * 사람이 읽는 형태로 풀어쓰고, 그 외 action은 원문 그대로 표시한다.
 */
function auditText(d: Record<string, any>): string {
  if (d.action === 'payment.activate_items') {
    const m = (d.metadata ?? {}) as {
      invitationIds?: string[]
      thankYouCardIds?: string[]
      // 슬롯 분리 개편 이후엔 객체({universal,invitation,thankyou}), 이전 옛 로그는 숫자(잔여 합계)
      remainingSlots?: number | { universal: number; invitation: number; thankyou: number }
    }
    const inv = m.invitationIds?.length ?? 0
    const ty = m.thankYouCardIds?.length ?? 0
    const parts: string[] = []
    if (inv) parts.push(`청첩장 ${inv}개`)
    if (ty) parts.push(`감사장 ${ty}개`)
    const head = `항목 활성화 — ${parts.length ? parts.join(' · ') : '대상 없음'}`
    const r = m.remainingSlots
    let tail = ''
    if (typeof r === 'number') tail = ` (잔여 슬롯 ${r})`
    else if (r) tail = ` (잔여 슬롯 범용 ${r.universal} · 청첩장 ${r.invitation} · 감사장 ${r.thankyou})`
    return head + tail
  }
  if (d.action === 'admin.user.activate_items') {
    const m = (d.metadata ?? {}) as { invitationIds?: string[]; thankYouCardIds?: string[] }
    const inv = m.invitationIds?.length ?? 0
    const ty = m.thankYouCardIds?.length ?? 0
    const parts: string[] = []
    if (inv) parts.push(`청첩장 ${inv}개`)
    if (ty) parts.push(`감사장 ${ty}개`)
    return `[어드민] 항목 활성화 — ${parts.length ? parts.join(' · ') : '대상 없음'} (슬롯 미차감)`
  }
  if (d.action === 'admin.user.adjust_slots') {
    const m = (d.metadata ?? {}) as { slotType?: string; amount?: number }
    const typeLabel = m.slotType === 'universal' ? '범용' : m.slotType === 'invitation' ? '청첩장' : '감사장'
    const amt = m.amount ?? 0
    return `[어드민] 슬롯 ${amt >= 0 ? '부여' : '회수'} — ${typeLabel} ${amt > 0 ? '+' : ''}${amt}`
  }
  return `${d.action} · ${d.status}`
}

interface UsersResponse {
  users: AdminUser[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

interface UserStatsData {
  newUsers: number
  usersWithInvitation: number
  usersWithThankyou: number
  usersWithAiGeneration: number
  usersWithNothing: number
  usersWithPayment: number
}

/** 통계 카드 드릴다운 세그먼트. 'all'=가입자 전체(세그먼트 필터 없음) */
type Segment = 'all' | 'invitation' | 'thankyou' | 'ai' | 'nothing' | 'paid'

const SEGMENT_TITLES: Record<Segment, string> = {
  all: '가입자',
  invitation: '청첩장 만든 사람',
  ai: 'AI 만든 사람',
  thankyou: '감사장 만든 사람',
  nothing: '아무것도 안만든 사람',
  paid: '결제한 사람',
}

/** 거래내역 drawer의 항목 활성화 패널에 표시할 청첩장/감사장 행 */
interface ItemRow {
  id: string
  label: string | null
  slug: string | null
  status: string
  activated: boolean
  expiresAt: string | null
}

type SlotType = 'universal' | 'invitation' | 'thankyou'

type ActionType = 'grant_credits' | 'revoke_credits' | 'set_admin' | 'set_user' | 'adjust_slots'

export function Users() {
  const { filters, setFilters, resetFilters } = useAdminFilters()
  const { addToast } = useToast()
  const [searchInput, setSearchInput] = useState('')
  // 디폴트 기간 = 어제 (FIKET-222)
  const defaultRange = useRef(yesterday()).current
  // 임시 날짜 (DateRangePicker 선택 상태) — 검색 버튼 클릭 시 applied에 반영
  const [pendingRange, setPendingRange] = useState<DateRange | null>({ from: defaultRange.from, to: defaultRange.to })
  // 실제 API에 전달되는 날짜
  const [startDate, setStartDate] = useState(defaultRange.from)
  const [endDate, setEndDate] = useState(defaultRange.to)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // 페이지 진입 시 필터 초기화 (다른 Admin 페이지의 상태가 남는 문제 방지)
  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  const [searchParams, setSearchParams] = useSearchParams()
  // 매출 관리에서 딥링크 진입(?historyUserId=…) 시 거래내역 Drawer를 바로 연다.
  const [historyUserId, setHistoryUserId] = useState<string | null>(() => searchParams.get('historyUserId'))
  // 통계 카드 클릭 시 열리는 세그먼트 유저 목록 Drawer
  const [segment, setSegment] = useState<Segment | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [actionType, setActionType] = useState<ActionType>('grant_credits')
  const [creditAmount, setCreditAmount] = useState(10)
  const [revokeReason, setRevokeReason] = useState('')
  const [slotType, setSlotType] = useState<SlotType>('universal')
  const [slotAmount, setSlotAmount] = useState(1) // 양수=부여 / 음수=회수
  const [activatingId, setActivatingId] = useState<string | null>(null) // 항목별 버튼 스피너

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortBy', filters.sortBy)
  params.set('sortOrder', filters.sortOrder)
  if (filters.search) params.set('search', filters.search)
  if (startDate) params.set('startDate', startDate)
  if (endDate) params.set('endDate', endDate)

  const { data, isLoading, error, refetch } = useApiFetch<UsersResponse>(
    `/api/admin/users?${params.toString()}`
  )

  // 기간 필터 미적용 시 전체 누적(가입일 기준, 기간 제한 없음)으로 통계 표시.
  const statsFrom = startDate || undefined
  const statsTo = endDate || undefined
  const statsQuery = new URLSearchParams()
  if (statsFrom) statsQuery.set('from', statsFrom)
  if (statsTo) statsQuery.set('to', statsTo)
  const { data: userStats } = useApiFetch<UserStatsData>(
    `/api/admin/dashboard/user-stats?${statsQuery.toString()}`
  )

  // 세그먼트 Drawer — 카드와 동일하게 가입일 코호트(statsFrom~statsTo, 미지정 시 전체 누적) 내
  // 유저 중 해당 활동을 (시점 무관) 한 사람을 필터링해 숫자와 목록을 일치시킨다.
  const segmentParams = new URLSearchParams()
  segmentParams.set('page', '1')
  segmentParams.set('pageSize', '100')
  segmentParams.set('sortBy', 'createdAt')
  segmentParams.set('sortOrder', 'desc')
  if (statsFrom) segmentParams.set('startDate', statsFrom)
  if (statsTo) segmentParams.set('endDate', statsTo)
  if (segment && segment !== 'all') segmentParams.set('segment', segment)
  const { data: segmentData, isLoading: segmentLoading } = useApiFetch<UsersResponse>(
    segment ? `/api/admin/users?${segmentParams.toString()}` : null
  )

  const { trigger, isLoading: actionLoading } = useApiMutation('/api/admin/users')

  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } = useApiFetch<{
    timeline: Array<{ type: string; timestamp: string; data: Record<string, any> }>
    user: {
      id: string
      email: string
      name: string | null
      aiCredits: number
      slots: Slots
      counts: {
        invitations: number
        invitationsActivated: number
        thankYouCards: number
        thankYouCardsActivated: number
      }
    }
    items: {
      invitations: ItemRow[]
      thankYouCards: ItemRow[]
    }
    pagination: { page: number; pageSize: number; total: number; totalPages: number }
  }>(historyUserId ? `/api/admin/users/${historyUserId}/transaction-history` : null)

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchInput(val)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setFilters({ search: val || undefined, page: 1 })
    }, 300)
  }, [setFilters])

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const handlePageChange = useCallback((page: number) => {
    setFilters({ page })
  }, [setFilters])

  const openActionModal = useCallback((user: AdminUser, action: ActionType) => {
    setSelectedUser(user)
    setActionType(action)
    setCreditAmount(10)
    setRevokeReason('')
    setSlotType('universal')
    setSlotAmount(1)
    setModalOpen(true)
  }, [])

  const executeAction = useCallback(async () => {
    if (!selectedUser) return

    const body: Record<string, unknown> = {
      action: actionType,
      userId: selectedUser.id,
    }
    if (actionType === 'grant_credits') {
      body.credits = creditAmount
    }
    if (actionType === 'revoke_credits') {
      if (!revokeReason.trim()) {
        addToast({ type: 'error', message: '회수 사유를 입력하세요.' })
        return
      }
      body.credits = creditAmount
      body.reason = revokeReason.trim()
    }
    if (actionType === 'adjust_slots') {
      if (!Number.isInteger(slotAmount) || slotAmount === 0) {
        addToast({ type: 'error', message: '0이 아닌 정수를 입력하세요.' })
        return
      }
      body.slotType = slotType
      body.amount = slotAmount
    }

    const result = await trigger(body)
    if (result) {
      addToast({ type: 'success', message: '처리되었습니다.' })
      setModalOpen(false)
      refetch()
    } else {
      addToast({ type: 'error', message: '처리에 실패했습니다.' })
    }
  }, [selectedUser, actionType, creditAmount, revokeReason, slotType, slotAmount, trigger, addToast, refetch])

  // 거래내역 drawer에서 특정 청첩장/감사장을 어드민 오버라이드로 직접 활성화 (슬롯 미차감)
  const activateItem = useCallback(
    async (kind: 'invitation' | 'thankyou', id: string) => {
      if (!historyUserId) return
      setActivatingId(id)
      const result = await trigger({
        action: 'activate_items',
        userId: historyUserId,
        invitationIds: kind === 'invitation' ? [id] : [],
        thankYouCardIds: kind === 'thankyou' ? [id] : [],
      })
      setActivatingId(null)
      if (result) {
        addToast({ type: 'success', message: '활성화되었습니다.' })
        refetchHistory()
        refetch()
      } else {
        addToast({ type: 'error', message: '활성화에 실패했습니다.' })
      }
    },
    [historyUserId, trigger, addToast, refetchHistory, refetch],
  )

  const columns: TableColumn<AdminUser>[] = [
    { key: 'name', label: '이름', sortable: true, render: (row) => row.name ?? '-', width: '100px' },
    { key: 'email', label: '이메일', sortable: true, width: '180px' },
    { key: 'role', label: '역할', render: (row) => <StatusBadge status={row.role} />, width: '70px' },
    { key: 'aiCredits', label: 'AI 크레딧', sortable: true, render: (row) => formatCredits(row.aiCredits), align: 'right' as const, width: '90px' },
    {
      key: 'slots',
      label: '활성화 슬롯',
      width: '150px',
      render: (row) => (
        <span title={slotsText(row.slots)} style={{ fontSize: 'var(--ds-font-size-xs)', whiteSpace: 'nowrap' }}>
          {slotsText(row.slots)}
        </span>
      ),
    },
    {
      key: 'invitations',
      label: '청첩장(활성/전체)',
      sortable: true,
      render: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <strong>{formatNumber(row._count.invitationsActivated)}</strong>
          <span style={{ color: 'var(--ds-color-text-tertiary)' }}> / {formatNumber(row._count.invitations)}</span>
        </span>
      ),
      align: 'right' as const,
      width: '110px',
    },
    {
      key: 'thankYouCards',
      label: '감사장(활성/전체)',
      render: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <strong>{formatNumber(row._count.thankYouCardsActivated)}</strong>
          <span style={{ color: 'var(--ds-color-text-tertiary)' }}> / {formatNumber(row._count.thankYouCards)}</span>
        </span>
      ),
      align: 'right' as const,
      width: '110px',
    },
    {
      key: 'aiGenerations',
      label: (
        <Tooltip content="생성: 누적 AI 사진 생성 수(삭제해도 유지) / 보유: 현재 남아있는 사진 수">
          <span style={{ whiteSpace: 'nowrap' }}>AI 생성 <span className={styles.hintMark}>ⓘ</span></span>
        </Tooltip>
      ),
      sortable: true,
      render: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <strong>{formatNumber(row._count.aiGenerations)}</strong>
          <span style={{ color: 'var(--ds-color-text-tertiary)' }}> / {formatNumber(row._count.aiRetained)}</span>
        </span>
      ),
      align: 'right' as const,
      width: '100px',
    },
    { key: 'createdAt', label: '가입일', sortable: true, render: (row) => formatDate(row.createdAt, 'yyyy.M.d'), width: '90px' },
    {
      key: 'actions',
      label: '',
      width: '50px',
      render: (row) => {
        const items: DropdownItem[] = [
          { label: '거래내역', onClick: () => setHistoryUserId(row.id) },
          { label: '크레딧 부여', onClick: () => openActionModal(row, 'grant_credits') },
          { label: '크레딧 회수', danger: true, onClick: () => openActionModal(row, 'revoke_credits') },
          { label: '슬롯 조정', onClick: () => openActionModal(row, 'adjust_slots') },
          { label: row.role === 'ADMIN' ? '일반으로 변경' : '관리자로 변경', onClick: () => openActionModal(row, row.role === 'ADMIN' ? 'set_user' : 'set_admin') },
        ]
        return (
          <Dropdown
            trigger={<button type="button" className={styles.menuBtn}>⋮</button>}
            items={items}
            align="right"
          />
        )
      },
    },
  ]

  // 세그먼트 Drawer용 컬럼 — 액션 드롭다운 없이 요약 정보 + 거래내역 바로가기
  const segmentColumns: TableColumn<AdminUser>[] = [
    { key: 'name', label: '이름', render: (row) => row.name ?? '-', width: '100px' },
    { key: 'email', label: '이메일', width: '200px' },
    {
      key: 'invitations',
      label: '청첩장',
      render: (row) => formatNumber(row._count.invitations),
      align: 'right' as const,
      width: '70px',
    },
    {
      key: 'thankYouCards',
      label: '감사장',
      render: (row) => formatNumber(row._count.thankYouCards),
      align: 'right' as const,
      width: '70px',
    },
    {
      key: 'aiGenerations',
      label: 'AI 생성(누적/보유)',
      render: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <strong>{formatNumber(row._count.aiGenerations)}</strong>
          <span style={{ color: 'var(--ds-color-text-tertiary)' }}> / {formatNumber(row._count.aiRetained)}</span>
        </span>
      ),
      align: 'right' as const,
      width: '110px',
    },
    { key: 'createdAt', label: '가입일', render: (row) => formatDate(row.createdAt, 'yyyy.M.d'), width: '90px' },
    {
      key: 'history',
      label: '',
      width: '80px',
      render: (row) => (
        <Button size="sm" variant="secondary" onClick={() => { setSegment(null); setHistoryUserId(row.id) }}>
          거래내역
        </Button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <Input
          size="sm"
          placeholder="이름 또는 이메일 검색..."
          value={searchInput}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />
        <DateRangePicker
          size="sm"
          value={pendingRange}
          presets={SHARED_RANGE_PRESETS_WITH_ALL}
          onChange={(range, { presetKey }) => {
            // '전체' 프리셋 = 기간 필터 해제
            setPendingRange(presetKey === ALL_PRESET_KEY ? null : range)
          }}
        />
        <Button
          size="sm"
          onClick={() => {
            setStartDate(pendingRange?.from ?? '')
            setEndDate(pendingRange?.to ?? '')
            setFilters({ page: 1 })
          }}
        >
          검색
        </Button>
        {(startDate || endDate) && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setPendingRange(null)
              setStartDate('')
              setEndDate('')
              setFilters({ page: 1 })
            }}
          >
            초기화
          </Button>
        )}
        {data?.pagination && (
          <span className={styles.count}>총 {formatNumber(data.pagination.total)}명</span>
        )}
      </div>

      {/* 회원 행동 통계 카드 — 기간 필터 연동. 클릭 시 세그먼트 유저 목록 Drawer */}
      <div className={styles.statsGrid}>
        <button type="button" className={cn(styles.statCard, styles.statCardClickable)} onClick={() => setSegment('all')}>
          <p className={styles.statLabel}>가입자수</p>
          <p className={styles.statValue}>{userStats ? formatNumber(userStats.newUsers) : '—'}</p>
        </button>
        <button type="button" className={cn(styles.statCard, styles.statCardClickable)} onClick={() => setSegment('invitation')}>
          <p className={styles.statLabel}>청첩장 만든 사람</p>
          <p className={styles.statValue}>
            {userStats ? formatNumber(userStats.usersWithInvitation) : '—'}
            {userStats && pct(userStats.usersWithInvitation, userStats.newUsers) && (
              <span className={styles.statPercent}> ({pct(userStats.usersWithInvitation, userStats.newUsers)})</span>
            )}
          </p>
        </button>
        <button type="button" className={cn(styles.statCard, styles.statCardClickable)} onClick={() => setSegment('ai')}>
          <p className={styles.statLabel}>AI 만든 사람</p>
          <p className={styles.statValue}>
            {userStats ? formatNumber(userStats.usersWithAiGeneration) : '—'}
            {userStats && pct(userStats.usersWithAiGeneration, userStats.newUsers) && (
              <span className={styles.statPercent}> ({pct(userStats.usersWithAiGeneration, userStats.newUsers)})</span>
            )}
          </p>
        </button>
        <button type="button" className={cn(styles.statCard, styles.statCardClickable)} onClick={() => setSegment('thankyou')}>
          <p className={styles.statLabel}>감사장 만든 사람</p>
          <p className={styles.statValue}>
            {userStats ? formatNumber(userStats.usersWithThankyou) : '—'}
            {userStats && pct(userStats.usersWithThankyou, userStats.newUsers) && (
              <span className={styles.statPercent}> ({pct(userStats.usersWithThankyou, userStats.newUsers)})</span>
            )}
          </p>
        </button>
        <button type="button" className={cn(styles.statCard, styles.statCardClickable)} onClick={() => setSegment('nothing')}>
          <p className={styles.statLabel}>아무것도 안만든 사람</p>
          <p className={styles.statValue}>
            {userStats ? formatNumber(userStats.usersWithNothing) : '—'}
            {userStats && pct(userStats.usersWithNothing, userStats.newUsers) && (
              <span className={styles.statPercent}> ({pct(userStats.usersWithNothing, userStats.newUsers)})</span>
            )}
          </p>
        </button>
        <button type="button" className={cn(styles.statCard, styles.statCardClickable)} onClick={() => setSegment('paid')}>
          <p className={styles.statLabel}>결제한 사람</p>
          <p className={styles.statValue}>
            {userStats ? formatNumber(userStats.usersWithPayment) : '—'}
            {userStats && pct(userStats.usersWithPayment, userStats.newUsers) && (
              <span className={styles.statPercent}> (가입자 대비 {pct(userStats.usersWithPayment, userStats.newUsers)})</span>
            )}
          </p>
          {userStats && pct(userStats.usersWithPayment, userStats.usersWithInvitation) && (
            <p className={styles.statPercentLine}>
              청첩장 제작자 대비 {pct(userStats.usersWithPayment, userStats.usersWithInvitation)}
            </p>
          )}
        </button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AdminUser>
        columns={columns}
        data={data?.users ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={handlePageChange}
          size="sm"
        />
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="유저 액션" size="sm">
        {selectedUser && (
          <div className={styles.modalContent}>
            <p className={styles.modalUser}>{selectedUser.name ?? selectedUser.email}</p>

            {actionType === 'grant_credits' && (
              <Input
                label="크레딧 수량"
                type="number"
                value={String(creditAmount)}
                onChange={(e) => setCreditAmount(Number(e.target.value))}
              />
            )}
            {actionType === 'revoke_credits' && (
              <>
                <p>보유 크레딧 <Badge color="gray">{selectedUser.aiCredits}개</Badge> 중 회수합니다. 보유 잔액까지만 회수됩니다.</p>
                <Input
                  label="회수 수량"
                  type="number"
                  value={String(creditAmount)}
                  onChange={(e) => setCreditAmount(Number(e.target.value))}
                />
                <Input
                  label="회수 사유 (필수)"
                  placeholder="예: 환불 처리, 오지급 회수"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                />
              </>
            )}
            {actionType === 'adjust_slots' && (
              <>
                <p style={{ fontSize: 'var(--ds-font-size-sm)', color: 'var(--ds-color-text-secondary)' }}>
                  현재 슬롯 — {slotsText(selectedUser.slots)}
                </p>
                <Select
                  label="슬롯 종류"
                  value={slotType}
                  onChange={(e) => setSlotType(e.target.value as SlotType)}
                  options={[
                    { value: 'universal', label: '범용' },
                    { value: 'invitation', label: '청첩장 전용' },
                    { value: 'thankyou', label: '감사장 전용' },
                  ]}
                />
                <Input
                  label="수량 (음수 입력 시 회수, 잔액까지만)"
                  type="number"
                  value={String(slotAmount)}
                  onChange={(e) => setSlotAmount(Number(e.target.value))}
                />
              </>
            )}
            {actionType === 'set_admin' && <p>이 유저를 <Badge color="red">ADMIN</Badge> 으로 변경합니다.</p>}
            {actionType === 'set_user' && <p>이 유저를 <Badge color="gray">USER</Badge> 로 변경합니다.</p>}

            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>취소</Button>
              <Button loading={actionLoading} onClick={executeAction}>확인</Button>
            </div>
          </div>
        )}
      </Modal>

      <Drawer
        open={!!historyUserId}
        onClose={() => {
          setHistoryUserId(null)
          // 딥링크로 들어온 경우 URL 파라미터도 정리 — 새로고침 시 닫은 Drawer가 다시 열리거나
          // 이후 다른 유저를 열었을 때 틀린 유저가 열리는 것 방지
          if (searchParams.has('historyUserId')) {
            const next = new URLSearchParams(searchParams)
            next.delete('historyUserId')
            setSearchParams(next, { replace: true })
          }
        }}
        title="거래내역"
        size="lg"
      >
        {historyLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--ds-space-8)' }}><Spinner /></div>
        ) : historyData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-4)' }}>
            {/* User summary */}
            <div style={{ padding: 'var(--ds-space-4)', background: 'var(--ds-neutral-50)', borderRadius: 'var(--ds-radius-md)', display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-2)' }}>
              <div style={{ fontWeight: 600, fontSize: 'var(--ds-font-size-sm)' }}>{historyData.user.name ?? historyData.user.email}</div>
              <div style={{ fontSize: 'var(--ds-font-size-xs)', color: 'var(--ds-color-text-tertiary)' }}>크레딧: {historyData.user.aiCredits}개</div>
              {/* 활성화 슬롯 — 범용/청첩장전용/감사장전용 */}
              <div style={{ display: 'flex', gap: 'var(--ds-space-2)', flexWrap: 'wrap' }}>
                <Badge color="gray">활성화 슬롯 (잔여 {totalSlots(historyData.user.slots)})</Badge>
                <Badge color="blue">범용 {historyData.user.slots.universal}</Badge>
                <Badge color="green">청첩장 {historyData.user.slots.invitation}</Badge>
                <Badge color="yellow">감사장 {historyData.user.slots.thankyou}</Badge>
              </div>
              {/* 청첩장·감사장 — 활성화/전체 개수 */}
              <div style={{ display: 'flex', gap: 'var(--ds-space-2)', flexWrap: 'wrap' }}>
                <Badge color="green">청첩장 활성 {historyData.user.counts.invitationsActivated} / 전체 {historyData.user.counts.invitations}</Badge>
                <Badge color="yellow">감사장 활성 {historyData.user.counts.thankYouCardsActivated} / 전체 {historyData.user.counts.thankYouCards}</Badge>
              </div>
            </div>

            {/* 항목 활성화 — 어드민 오버라이드(슬롯 미차감) */}
            <div style={{ padding: 'var(--ds-space-4)', border: '1px solid var(--ds-color-border)', borderRadius: 'var(--ds-radius-md)', display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-2)' }}>
              <div style={{ fontWeight: 600, fontSize: 'var(--ds-font-size-sm)' }}>항목 활성화 <span style={{ fontWeight: 400, color: 'var(--ds-color-text-tertiary)' }}>(어드민 오버라이드 — 슬롯 미차감)</span></div>
              {historyData.items.invitations.length === 0 && historyData.items.thankYouCards.length === 0 ? (
                <div style={{ color: 'var(--ds-color-text-tertiary)', fontSize: 'var(--ds-font-size-sm)' }}>활성화할 항목이 없습니다</div>
              ) : (
                <>
                  {historyData.items.invitations.map((it) => (
                    <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ds-space-2)', fontSize: 'var(--ds-font-size-sm)' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Badge color="blue">청첩장</Badge> {it.label ?? it.slug ?? it.id}
                        {it.status === 'DELETED' && <span style={{ color: 'var(--ds-color-text-tertiary)' }}> (삭제됨)</span>}
                      </span>
                      {it.activated ? (
                        <Badge color="green">활성화됨</Badge>
                      ) : (
                        <Button size="sm" loading={activatingId === it.id} onClick={() => activateItem('invitation', it.id)}>활성화</Button>
                      )}
                    </div>
                  ))}
                  {historyData.items.thankYouCards.map((it) => (
                    <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ds-space-2)', fontSize: 'var(--ds-font-size-sm)' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Badge color="yellow">감사장</Badge> {it.label ?? it.slug ?? it.id}
                      </span>
                      {it.activated ? (
                        <Badge color="green">활성화됨</Badge>
                      ) : (
                        <Button size="sm" loading={activatingId === it.id} onClick={() => activateItem('thankyou', it.id)}>활성화</Button>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Timeline */}
            {historyData.timeline.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 'var(--ds-space-8)', color: 'var(--ds-color-text-tertiary)', fontSize: 'var(--ds-font-size-sm)' }}>거래내역이 없습니다</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-2)' }}>
                {historyData.timeline.map((item, i) => {
                  // 항목 활성화 audit는 별도 라벨/색으로 구분 (유저 결제 활성화 + 어드민 오버라이드 둘 다)
                  const auditAction = item.type === 'audit' ? (item.data as any).action : undefined
                  const isActivation = auditAction === 'payment.activate_items' || auditAction === 'admin.user.activate_items'
                  const badgeColor = item.type === 'payment' ? 'blue' : item.type === 'credit' ? 'green' : isActivation ? 'yellow' : 'gray'
                  const badgeLabel = item.type === 'payment' ? '결제' : item.type === 'credit' ? '크레딧' : isActivation ? '활성화' : '감사로그'
                  return (
                  <div key={i} style={{ padding: 'var(--ds-space-3) var(--ds-space-4)', border: '1px solid var(--ds-color-border)', borderRadius: 'var(--ds-radius-md)', fontSize: 'var(--ds-font-size-sm)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--ds-space-1)' }}>
                      <Badge color={badgeColor}>{badgeLabel}</Badge>
                      <span style={{ color: 'var(--ds-color-text-tertiary)', fontSize: 'var(--ds-font-size-xs)' }}>
                        {formatDate(item.timestamp, 'yyyy.M.d HH:mm')}
                      </span>
                    </div>
                    <div style={{ color: 'var(--ds-color-text-secondary)' }}>
                      {item.type === 'payment' && `${(item.data as any).paymentType} · ${formatCurrency((item.data as any).amount)} · ${(item.data as any).status}`}
                      {item.type === 'credit' && (() => {
                        const d = item.data as { txType: string; amount: number; balanceAfter: number }
                        const negative = d.txType === 'DEDUCT' || d.txType === 'REFUND'
                        const signed = negative ? -d.amount : d.amount
                        return `${d.txType} · ${signed > 0 ? '+' : ''}${signed}개 → ${d.balanceAfter}개`
                      })()}
                      {item.type === 'audit' && auditText(item.data as any)}
                    </div>
                    {/* 결제 — 주문번호 + 지급 크레딧 */}
                    {item.type === 'payment' && (() => {
                      const orderNo = orderNoText(item.data as any)
                      const credits = (item.data as any).creditsGranted
                      if (!orderNo && credits == null) return null
                      return (
                        <div style={{ color: 'var(--ds-color-text-tertiary)', fontSize: 'var(--ds-font-size-xs)', marginTop: 'var(--ds-space-1)' }}>
                          {orderNo && `주문번호 ${orderNo}`}
                          {orderNo && credits != null && ' · '}
                          {credits != null && `크레딧 +${credits}`}
                        </div>
                      )
                    })()}
                    {item.type === 'credit' && (item.data as any).description && (
                      <div style={{ color: 'var(--ds-color-text-tertiary)', fontSize: 'var(--ds-font-size-xs)', marginTop: 'var(--ds-space-1)' }}>
                        {(item.data as any).description}
                      </div>
                    )}
                  </div>
                  )
                })}
              </div>
            )}

            {/* Print button */}
            <Button variant="secondary" size="sm" onClick={() => window.print()}>
              인쇄 / PDF 저장
            </Button>
          </div>
        ) : null}
      </Drawer>

      {/* 세그먼트 유저 목록 Drawer — 통계 카드 클릭 시 */}
      <Drawer
        open={!!segment}
        onClose={() => setSegment(null)}
        title={segment ? SEGMENT_TITLES[segment] : ''}
        width="min(95vw, 900px)"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-3)' }}>
          <div style={{ fontSize: 'var(--ds-font-size-sm)', color: 'var(--ds-color-text-secondary)' }}>
            {statsFrom || statsTo ? `${statsFrom ?? '전체'} ~ ${statsTo ?? '전체'}` : '전체 기간'} 가입 기준
            {segmentData?.pagination && <> · 총 {formatNumber(segmentData.pagination.total)}명</>}
            {segmentData?.pagination && segmentData.pagination.total > segmentData.users.length && (
              <> (최대 {formatNumber(segmentData.users.length)}명 표시)</>
            )}
          </div>
          <Table<AdminUser>
            columns={segmentColumns}
            data={segmentData?.users ?? []}
            rowKey={(row) => row.id}
            isLoading={segmentLoading}
            emptyMessage="해당하는 회원이 없습니다"
          />
        </div>
      </Drawer>
    </div>
  )
}
